// Admin blacklist slice. Loads the email blacklist + allowlist and handles
// add/delete for each. The two lists share one `saving`/`saveError` pair (one
// in-flight write at a time) and one `loading`/`error` pair for the initial
// fetch, mirroring the admin-catalog slice.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminFetch, AdminAuthError } from '../api/admin-client.js';
import { clearAdminSession } from './admin-auth-slice.js';

const PATHS = {
  blacklist: '/api/admin/blacklist',
  allowlist: '/api/admin/allowlist',
};

const handleThunkError = (err, dispatch, rejectWithValue) => {
  if (err instanceof AdminAuthError) dispatch(clearAdminSession());
  return rejectWithValue(err.message);
};

export const fetchEmailLists = createAsyncThunk(
  'adminBlacklist/fetch',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      const [blacklist, allowlist] = await Promise.all([
        adminFetch(PATHS.blacklist),
        adminFetch(PATHS.allowlist),
      ]);
      return { blacklist: blacklist.items, allowlist: allowlist.items };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

export const addEmailEntry = createAsyncThunk(
  'adminBlacklist/add',
  async ({ list, values }, { dispatch, rejectWithValue }) => {
    try {
      const { item } = await adminFetch(PATHS[list], { method: 'POST', body: values });
      return { list, item };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

export const deleteEmailEntry = createAsyncThunk(
  'adminBlacklist/delete',
  async ({ list, id }, { dispatch, rejectWithValue }) => {
    try {
      await adminFetch(`${PATHS[list]}/${id}`, { method: 'DELETE' });
      return { list, id };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Loads the total count + last-refresh time of the seeded disposable/temp-mail
// list (no patterns) so the page can show its size without pulling 70k+ rows.
export const fetchSeedCount = createAsyncThunk(
  'adminBlacklist/seedCount',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      const { count, refreshedAt } = await adminFetch('/api/admin/blacklist-seed');
      return { count, refreshedAt };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Asks the server to re-download the maintained disposable-domain list and
// replace the seeded rows. Long-running (network + bulk insert).
export const refreshSeedList = createAsyncThunk(
  'adminBlacklist/seedRefresh',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      const { count, refreshedAt } = await adminFetch('/api/admin/blacklist-seed/refresh', { method: 'POST' });
      return { count, refreshedAt };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Searches the seeded list for a substring; the server caps the result set.
export const searchSeedList = createAsyncThunk(
  'adminBlacklist/seedSearch',
  async (term, { dispatch, rejectWithValue }) => {
    try {
      const query = `?q=${encodeURIComponent(term)}`;
      const { count, matches } = await adminFetch(`/api/admin/blacklist-seed${query}`);
      return { count, matches };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

// Loads the read-only seeded defaults that ship with the deployment: the
// allowlist defaults (*.gov / *.mil / *.us / sanborn.com) and the default
// blocked competitor domains. Managed via seeds, surfaced here for transparency.
export const fetchSeededDefaults = createAsyncThunk(
  'adminBlacklist/seededDefaults',
  async (_arg, { dispatch, rejectWithValue }) => {
    try {
      const [allowlist, competitors] = await Promise.all([
        adminFetch('/api/admin/allowlist-defaults'),
        adminFetch('/api/admin/blacklist-competitors'),
      ]);
      return { allowlist: allowlist.items, competitors: competitors.items };
    } catch (err) {
      return handleThunkError(err, dispatch, rejectWithValue);
    }
  },
);

const byTypeThenPattern = (a, b) => (
  a.pattern_type.localeCompare(b.pattern_type) || a.pattern.localeCompare(b.pattern)
);

const initialState = {
  data: { blacklist: [], allowlist: [] },
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  // Read-only view of the seeded disposable/temp-mail list.
  seed: {
    count: null,
    refreshedAt: null,
    query: '',
    matches: [],
    searching: false,
    searchError: null,
    refreshing: false,
    refreshError: null,
  },
  // Read-only seeded defaults (allowlist defaults + competitor blacklist).
  defaults: {
    allowlist: [],
    competitors: [],
    loading: false,
    error: null,
  },
};

const adminBlacklistSlice = createSlice({
  name: 'adminBlacklist',
  initialState,
  reducers: {
    clearSaveError: (s) => { s.saveError = null; },
    clearSeedSearch: (s) => {
      s.seed.query = '';
      s.seed.matches = [];
      s.seed.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailLists.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchEmailLists.fulfilled, (s, a) => {
        s.loading = false;
        s.data = a.payload;
      })
      .addCase(fetchEmailLists.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Could not load the email lists.';
      })
      .addCase(addEmailEntry.pending, (s) => {
        s.saving = true;
        s.saveError = null;
      })
      .addCase(addEmailEntry.fulfilled, (s, a) => {
        s.saving = false;
        s.data[a.payload.list] = [...s.data[a.payload.list], a.payload.item]
          .sort(byTypeThenPattern);
      })
      .addCase(addEmailEntry.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Could not add the entry.';
      })
      .addCase(deleteEmailEntry.fulfilled, (s, a) => {
        s.data[a.payload.list] = s.data[a.payload.list]
          .filter((row) => row.id !== a.payload.id);
      })
      .addCase(deleteEmailEntry.rejected, (s, a) => {
        s.error = a.payload || 'Could not delete the entry.';
      })
      .addCase(fetchSeedCount.fulfilled, (s, a) => {
        s.seed.count = a.payload.count;
        s.seed.refreshedAt = a.payload.refreshedAt;
      })
      .addCase(refreshSeedList.pending, (s) => {
        s.seed.refreshing = true;
        s.seed.refreshError = null;
      })
      .addCase(refreshSeedList.fulfilled, (s, a) => {
        s.seed.refreshing = false;
        s.seed.count = a.payload.count;
        s.seed.refreshedAt = a.payload.refreshedAt;
      })
      .addCase(refreshSeedList.rejected, (s, a) => {
        s.seed.refreshing = false;
        s.seed.refreshError = a.payload || 'Could not refresh the list.';
      })
      .addCase(searchSeedList.pending, (s, a) => {
        s.seed.searching = true;
        s.seed.searchError = null;
        s.seed.query = a.meta.arg;
      })
      .addCase(searchSeedList.fulfilled, (s, a) => {
        s.seed.searching = false;
        s.seed.count = a.payload.count;
        s.seed.matches = a.payload.matches;
      })
      .addCase(searchSeedList.rejected, (s, a) => {
        s.seed.searching = false;
        s.seed.searchError = a.payload || 'Could not search the list.';
      })
      .addCase(fetchSeededDefaults.pending, (s) => {
        s.defaults.loading = true;
        s.defaults.error = null;
      })
      .addCase(fetchSeededDefaults.fulfilled, (s, a) => {
        s.defaults.loading = false;
        s.defaults.allowlist = a.payload.allowlist;
        s.defaults.competitors = a.payload.competitors;
      })
      .addCase(fetchSeededDefaults.rejected, (s, a) => {
        s.defaults.loading = false;
        s.defaults.error = a.payload || 'Could not load the seeded defaults.';
      });
  },
});

export const { clearSaveError, clearSeedSearch } = adminBlacklistSlice.actions;
export default adminBlacklistSlice.reducer;
